/**
 * Sound + haptic feedback for game events.
 *
 * Tones are synthesised with the Web Audio API rather than shipped as audio files:
 * no binary assets in the bundle, and it works in offline play.
 *
 * Vibration is Android-only in practice — WebKit implements no Vibration API, so
 * `navigator.vibrate` is absent in every browser on iOS. Calls there simply no-op.
 */

export type FeedbackEvent = 'znk' | 'slice' | 'yourTurn';

const MUTE_STORAGE_KEY = 'znkFeedbackMuted';

let audioCtx: AudioContext | null = null;

export const isFeedbackMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  } catch {
    return false; // private-mode browsers can throw on localStorage access
  }
};

export const setFeedbackMuted = (muted: boolean): void => {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, String(muted));
  } catch {
    /* preference simply won't persist */
  }
};

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;

  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  if (!audioCtx) audioCtx = new Ctor();

  // Autoplay policy starts the context suspended until a user gesture; resuming
  // an already-running context is a no-op.
  if (audioCtx.state === 'suspended') void audioCtx.resume();

  return audioCtx;
};

interface Tone {
  freq: number;
  /** When set, the pitch ramps from `freq` to this value across the tone. */
  endFreq?: number;
  /** Offset from the start of the effect, in seconds. */
  start: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
}

const playTones = (tones: Tone[]): void => {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  tones.forEach(tone => {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();

    osc.type = tone.type ?? 'sine';
    osc.frequency.setValueAtTime(tone.freq, now + tone.start);
    if (tone.endFreq) {
      osc.frequency.exponentialRampToValueAtTime(tone.endFreq, now + tone.start + tone.duration);
    }

    // Ramp the gain instead of switching it: starting or stopping a raw oscillator
    // at full amplitude produces an audible click. exponentialRamp cannot reach 0,
    // hence the small non-zero floor.
    const peak = tone.gain ?? 0.18;
    amp.gain.setValueAtTime(0.0001, now + tone.start);
    amp.gain.exponentialRampToValueAtTime(peak, now + tone.start + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.duration);

    osc.connect(amp).connect(ctx.destination);
    osc.start(now + tone.start);
    osc.stop(now + tone.start + tone.duration + 0.02);
  });
};

const vibrate = (pattern: number | number[]): void => {
  if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* some browsers throw if the page is backgrounded */
  }
};

const EFFECTS: Record<FeedbackEvent, { tones: Tone[]; vibration: number | number[] }> = {
  // Rising major triad — the celebratory moment.
  znk: {
    tones: [
      { freq: 523.25, start: 0.0, duration: 0.13, type: 'triangle' },
      { freq: 659.25, start: 0.1, duration: 0.13, type: 'triangle' },
      { freq: 783.99, start: 0.2, duration: 0.3, type: 'triangle', gain: 0.22 },
    ],
    vibration: [60, 40, 120],
  },

  // Downward sweep, suggesting something cut through.
  slice: {
    tones: [{ freq: 880, endFreq: 220, start: 0, duration: 0.28, type: 'sawtooth', gain: 0.14 }],
    vibration: [40, 60, 40],
  },

  // Deliberately short and soft: this one fires on every single turn.
  yourTurn: {
    tones: [
      { freq: 987.77, start: 0.0, duration: 0.09 },
      { freq: 1318.51, start: 0.09, duration: 0.13 },
    ],
    vibration: 35,
  },
};

/**
 * Ambient background pad. Synthesised rather than looped from a file, so there is
 * no seam on repeat and no asset to download. Deliberately quiet and slow-moving:
 * it should sit under the game, not compete with the event cues above.
 */
interface BackgroundNodes {
  oscillators: OscillatorNode[];
  lfo: OscillatorNode;
  master: GainNode;
}

let background: BackgroundNodes | null = null;

// A minor triad spread across octaves — calm, and harmonically out of the way of
// the event cues, which sit up at 500-1300Hz.
const PAD_FREQUENCIES = [110, 164.81, 220, 329.63];

export const startBackgroundMusic = (): void => {
  if (isFeedbackMuted() || background) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.05, now + 3); // slow fade-in

  // Rolls off the harshness of the raw waveforms.
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(700, now);
  filter.Q.setValueAtTime(0.7, now);

  // Very slow sweep so the pad breathes instead of sitting static.
  const lfo = ctx.createOscillator();
  const lfoDepth = ctx.createGain();
  lfo.frequency.setValueAtTime(0.05, now); // one cycle every 20s
  lfoDepth.gain.setValueAtTime(220, now);
  lfo.connect(lfoDepth).connect(filter.frequency);
  lfo.start(now);

  const oscillators = PAD_FREQUENCIES.map((freq, i) => {
    const osc = ctx.createOscillator();
    const voice = ctx.createGain();
    osc.type = i === 0 ? 'sine' : 'triangle';
    osc.frequency.setValueAtTime(freq, now);
    // Slight detune keeps the voices from phasing into a single sterile tone.
    osc.detune.setValueAtTime((i - 1.5) * 4, now);
    voice.gain.setValueAtTime(i === 0 ? 0.5 : 0.25, now);
    osc.connect(voice).connect(filter);
    osc.start(now);
    return osc;
  });

  filter.connect(master).connect(ctx.destination);

  background = { oscillators, lfo, master };
};

export const stopBackgroundMusic = (): void => {
  if (!background) return;

  const ctx = getAudioContext();
  const { oscillators, lfo, master } = background;
  background = null;

  if (!ctx) return;

  const now = ctx.currentTime;
  // Fade out before stopping, or the cut produces a click.
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), now);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);

  [...oscillators, lfo].forEach(osc => {
    try {
      osc.stop(now + 1.3);
    } catch {
      /* already stopped */
    }
  });
};

export const playFeedback = (event: FeedbackEvent): void => {
  if (isFeedbackMuted()) return;
  const effect = EFFECTS[event];
  playTones(effect.tones);
  vibrate(effect.vibration);
};

// An AudioContext constructed before any user gesture stays suspended. Creating it
// on the first interaction means the first real effect plays without being swallowed.
if (typeof window !== 'undefined') {
  const unlock = (): void => {
    getAudioContext();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}
